import { useEffect, useState } from 'react';
import qs from 'qs';
import {
  IneligibleInvoiceFetchFilters,
  buildIneligibleInvoiceFetchFilter,
} from '@/enterprise/data/useIneligibleInvoices';
import {
  InvoiceAdjustmentFetchFilters,
  buildInvoiceAdjustmentFetchFilter,
} from '@/enterprise/data/useInvoiceAdjustments';
import escapeSpecialCharacters from '@/utils/escapeSpecialCharacters';
import getToken from './token';

const prefixUrl = import.meta.env.VITE_GATEWAY_URL;

interface CommonArgs {
  marketId: string;
  takerId: number;
}

export type EligibleInvoiceAmount = ',25000' | '25000,50000' | '50000,75000' | '75000,';
export type EligibleInvoiceDpe = '0,9' | '10,20' | '21,30' | '31,60' | '61,';
export interface EligibleInvoiceFetchFilters {
  included?: boolean[]; // takerExcluded
  clearing?: boolean[]; // isInCashPool
  dpe?: EligibleInvoiceDpe[]; // daysPaidEarly
  amount?: EligibleInvoiceAmount[];
  dueDate?: string[];
  search?: string; // invoiceId
}

type FilterTypeArgs =
  | {
      type: 'adjustments';
      filters?: InvoiceAdjustmentFetchFilters;
    }
  | {
      type: 'eligible';
      filters?: EligibleInvoiceFetchFilters;
    }
  | {
      type: 'ineligible';
      filters?: IneligibleInvoiceFetchFilters;
    };

type InvoiceDownloadUrlArgs = CommonArgs & FilterTypeArgs;

export const buildEligibleInvoiceFetchFilter = (filterOptions: EligibleInvoiceFetchFilters) => {
  if (!filterOptions) {
    return;
  }

  const filter = [];

  if (filterOptions.included) {
    if (
      filterOptions.included.length !== 2 ||
      (!filterOptions.included.includes(true) && !filterOptions.included.includes(false))
    ) {
      // !taker excluded means the invoice is included in offer
      filter.push([{ takerExcluded: !filterOptions.included[0] }]);
    }
  }

  if (filterOptions.clearing) {
    if (
      filterOptions.clearing.length !== 2 ||
      (!filterOptions.clearing.includes(true) && !filterOptions.clearing.includes(false))
    ) {
      filter.push([{ isInCashPool: filterOptions.clearing[0] }]);
    }
  }

  if (filterOptions.dpe) {
    const dpeRange: { daysPaidEarly: { from?: number; to?: number } }[] = [];

    filterOptions.dpe.forEach((o) => {
      const split = o.split(',');
      const range = {
        ...(split[0] && { from: Number(split[0]) }),
        ...(split[1] && { to: Number(split[1]) }),
      };
      dpeRange.push({ daysPaidEarly: range });
    });

    filter.push(dpeRange);
  }

  if (filterOptions.amount) {
    const amountRange: { amount: { from?: number; to?: number } }[] = [];

    filterOptions.amount.forEach((o) => {
      const split = o.split(',');
      const range = {
        ...(split[0] && { from: Number(split[0]) }),
        ...(split[1] && { to: Number(split[1]) }),
      };
      amountRange.push({ amount: range });
    });

    filter.push(amountRange);
  }

  if (filterOptions.dueDate) {
    const dueDateRange: { dueDate: { from?: string; to?: string } }[] = [];

    const split = filterOptions.dueDate[0].split(',');
    const range = {
      ...(split[0] && { from: split[0] }),
      ...(split[1] && { to: split[1] }),
    };
    dueDateRange.push({ dueDate: range });

    filter.push(dueDateRange);
  }

  if (filterOptions.search) {
    const invoiceId = escapeSpecialCharacters(filterOptions.search.toString());
    filter.push([{ invoiceId }]);
  }

  if (!filter.length) {
    return;
  }

  return { filter };
};

// Download invoices url used on Division Invoice page menu actions
const useInvoiceDownloadUrl = ({ filters, marketId, takerId, type }: InvoiceDownloadUrlArgs) => {
  const token = getToken();
  const [invoiceDownloadUrl, setInvoiceDownloadUrl] = useState<string>('');

  const baseInvoiceDownloadUrl = `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/invoices/${type}/csv`;

  useEffect(() => {
    if (filters) {
      const builtFilters =
        type === 'ineligible'
          ? buildIneligibleInvoiceFetchFilter(filters)
          : type === 'adjustments'
          ? buildInvoiceAdjustmentFetchFilter(filters)
          : buildEligibleInvoiceFetchFilter(filters);
      const params = qs.stringify(builtFilters);

      return setInvoiceDownloadUrl(`${baseInvoiceDownloadUrl}?token=${token}&${params}`);
    }

    return setInvoiceDownloadUrl(`${baseInvoiceDownloadUrl}?token=${token}`);
  }, [baseInvoiceDownloadUrl, filters, token, type]);

  return invoiceDownloadUrl;
};

export default useInvoiceDownloadUrl;
